// Module 3.2a — BHA (Bottom-Hole Assembly) schema (v1).
//
// One BHARecord = one physical assembly running on a well.
// Components are ordered from BIT outward (component[0] = bit, last item =
// top sub). Each component carries a PERMANENT componentUid that survives
// reorders / removals so future modules (inspections, maintenance history,
// photographs, failure analysis) can track a single piece of equipment
// across many BHAs.
//
// Auto-sequencing: `sequence` is overwritten by storage.js on every persist
// so duplicates / gaps are impossible by construction.
//
// Distance from bit is a READ-ONLY computed field derived by
// `computeBhaGeometry(...)` — never persisted.

export const BHA_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const ASSEMBLY_STATUSES = ["draft", "approved", "locked"];
export const OPERATIONAL_STATUSES = ["planned", "in-hole", "racked", "retired"];

// Component roles + which Module 2 category each maps to. "" means no
// library exists yet (the user enters specs manually for that component).
export const COMPONENT_ROLES = [
  { role: "bit",            label: "Bit",           refCategory: "bit"             },
  { role: "motor",          label: "Mud Motor",     refCategory: "mud_motor"       },
  { role: "rss",            label: "RSS",           refCategory: "rotary_steerable"},
  { role: "mwd",            label: "MWD",           refCategory: "mwd_tool"        },
  { role: "lwd",            label: "LWD",           refCategory: "lwd_tool"        },
  { role: "stabilizer",     label: "Stabilizer",    refCategory: "stabilizer"      },
  { role: "drill_collar",   label: "Drill Collar",  refCategory: "drill_collar"    },
  { role: "hwdp",           label: "HWDP",          refCategory: "hwdp"            },
  { role: "drill_pipe",     label: "Drill Pipe",    refCategory: "drill_pipe"      },
  { role: "crossover",      label: "Crossover",     refCategory: "crossover"       },
  { role: "jar",            label: "Jar",           refCategory: ""                },
  { role: "shock_sub",      label: "Shock Sub",     refCategory: ""                },
  { role: "other",          label: "Other",         refCategory: ""                },
];

export const VALID_ROLES = COMPONENT_ROLES.map((r) => r.role);
export function roleLabel(role) {
  const r = COMPONENT_ROLES.find((x) => x.role === role);
  return r ? r.label : role;
}
export function roleRefCategory(role) {
  const r = COMPONENT_ROLES.find((x) => x.role === role);
  return r ? r.refCategory : "";
}

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------
let _uidCounter = 0;
function _newComponentUid() {
  _uidCounter += 1;
  return `cmp_${Date.now()}_${_uidCounter}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyBhaComponent(role = "other") {
  return {
    componentUid: _newComponentUid(),  // permanent, survives reorder/removal
    sequence: 0,                       // auto-overwritten by storage on save
    role,
    refCategory: roleRefCategory(role),
    refId: "",
    displayName: "",
    outerDiameter: "",
    innerDiameter: "",
    lengthFt: "",
    weightPpf: "",
    // Total component weight, lbs. DERIVED for tubulars (length x weightPpf,
    // see computeBhaGeometry) and operator-entered for everything else.
    // Absent on BHAs saved before this field existed — reads as "" → "—".
    totalWeightLbs: "",
    connection: "",
    pieceCount: "",                    // for jointed tubulars (DP / HWDP / DC)
    notes: "",
  };
}

export function emptyBhaRecord() {
  return {
    id: "",
    schemaVersion: BHA_SCHEMA_VERSION,
    wellId: "",

    // Identity
    name: "",
    bhaNumber: "",
    runNumber: "",
    revision: "",                      // optional — "A", "B", "C" …
    bitSize: "",                       // hole size in inches

    // Two-track status
    assemblyStatus: "draft",
    operationalStatus: "planned",

    // Operating window
    startDateTime: "",
    endDateTime: "",
    depthInFt: "",
    depthOutFt: "",
    rotatingHours: "",
    totalHours: "",

    // Components — ordered from bit outward
    components: [],

    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

// ---------------------------------------------------------------------------
// Geometry — pure function. Returns a NEW record with read-only computed
// fields populated per component:
//
//   distanceFromBitFt  – cumulative length of every component BELOW this one
//                        (0 for the bit; the top of the bit for the motor; ...)
//   cumulativeLengthFt – top of this component from bit (distanceFromBit + own length)
//
// If any component below this one has a non-numeric length, downstream
// computations bail out and the field is set to "" so the UI can render "—"
// rather than a misleading partial total.
// ---------------------------------------------------------------------------
// Roles whose weight is a function of length (jointed tubulars), as opposed
// to a single tool with a manufacturer-stated weight.
export const TUBULAR_ROLES = ["drill_pipe", "hwdp", "drill_collar"];

// Roles with no per-foot weight — a bit is a single tool, not a length of
// pipe, so it only ever carries an operator-entered total weight.
export const NO_PPF_ROLES = ["bit"];

/**
 * True when a component's total weight is DERIVED (length x ppf) rather
 * than operator-entered. Requires both numbers: with a ppf but no length
 * there is nothing to multiply, so the entered total weight stands.
 * Shared by computeBhaGeometry and the editor so the stored value and the
 * read-only/editable state of the field can never disagree.
 */
export function weightIsDerived(c) {
  if (!c || NO_PPF_ROLES.includes(c.role)) return false;
  const len = Number(c.lengthFt);
  const ppf = Number(c.weightPpf);
  return Number.isFinite(len) && len > 0 && Number.isFinite(ppf) && ppf > 0;
}

// Nominal density of steel, ppg. Buoyancy factor = (steel - mud) / steel.
const STEEL_DENSITY_PPG = 65.5;

/**
 * Buoyancy factor for a mud weight in ppg, or null when it cannot be
 * computed. Returns null for mud at or above steel density — that is
 * physically impossible and would otherwise yield a zero or negative
 * buoyed weight presented as fact.
 */
export function buoyancyFactorFor(mudWeightPpg) {
  const mw = Number(mudWeightPpg);
  if (!Number.isFinite(mw) || mw <= 0 || mw >= STEEL_DENSITY_PPG) return null;
  return +((STEEL_DENSITY_PPG - mw) / STEEL_DENSITY_PPG).toFixed(4);
}

/**
 * @param {object} rec
 * @param {object} [opts]
 * @param {number|null} [opts.buoyancyFactor] when supplied, each component
 *        also gets `buoyedWeightLbs` and the record gets
 *        `totalBhaBuoyedWeightLbs`. Optional so existing single-argument
 *        callers (WellProfileEditor's BHA summary, the save path, tests)
 *        keep working untouched.
 */
export function computeBhaGeometry(rec, { buoyancyFactor = null } = {}) {
  if (!rec || !Array.isArray(rec.components)) return rec;
  let running = 0;
  let bailed = false;
  let totalWeight = 0;
  let anyWeight = false;
  let totalBuoyed = 0;
  let anyBuoyed = false;
  // Must be a POSITIVE finite number. Number("") is 0 — finite — so a loose
  // check would quietly turn a missing factor into a confident 0 lbs buoyed
  // weight on every component.
  const _bfNum = Number(buoyancyFactor);
  const bf = buoyancyFactor !== "" && buoyancyFactor != null &&
    Number.isFinite(_bfNum) && _bfNum > 0 ? _bfNum : null;

  const comps = rec.components.map((c) => {
    const out = { ...c };

    // ---- Stack-up (unchanged semantics) --------------------------------
    // Restructured from early `return out` into if/else so the weight block
    // below still runs for components after a bail — a component's own
    // weight does not depend on the cumulative chain.
    if (bailed) {
      out.distanceFromBitFt = "";
      out.cumulativeLengthFt = "";
    } else {
      out.distanceFromBitFt = running;
      const raw = c.lengthFt;
      // Empty / null / non-numeric / negative → cannot compute beyond this point.
      if (raw === "" || raw == null ||
          !Number.isFinite(Number(raw)) || Number(raw) < 0) {
        // Cannot compute beyond this point — leave own cumulative empty too.
        out.cumulativeLengthFt = "";
        bailed = true;
      } else {
        const len = Number(raw);
        running += len;
        out.cumulativeLengthFt = running;
      }
    }

    // ---- Component weight ----------------------------------------------
    // Any role that can carry a per-foot weight derives its total from
    // length x ppf, recomputed every pass and read-only in the UI. Without
    // both numbers the operator's own entered total weight stands — it is
    // never cleared, because for most tools that figure is the only weight
    // there is.
    if (weightIsDerived(c)) {
      out.totalWeightLbs = +(Number(c.lengthFt) * Number(c.weightPpf)).toFixed(2);
    }
    const w = Number(out.totalWeightLbs);
    const hasWeight = out.totalWeightLbs !== "" && out.totalWeightLbs != null &&
      Number.isFinite(w) && w > 0;
    if (hasWeight) {
      totalWeight += w;
      anyWeight = true;
    }

    // ---- Buoyed weight --------------------------------------------------
    // Display-only: depends on the CURRENT mud weight, which lives in the
    // Mud Program and changes independently of this BHA. Recomputed on every
    // pass and written as "" whenever no factor was supplied, so a stored
    // record can never carry a stale buoyed figure.
    if (hasWeight && bf != null) {
      out.buoyedWeightLbs = +(w * bf).toFixed(2);
      totalBuoyed += out.buoyedWeightLbs;
      anyBuoyed = true;
    } else {
      out.buoyedWeightLbs = "";
    }

    return out;
  });

  return {
    ...rec,
    components: comps,
    // "" rather than 0 when nothing contributed, so a BHA whose weights have
    // simply not been entered reads as "—" instead of a confident "0 lbs".
    totalBhaWeightLbs: anyWeight ? +totalWeight.toFixed(2) : "",
    totalBhaBuoyedWeightLbs: anyBuoyed ? +totalBuoyed.toFixed(2) : "",
  };
}

// ---------------------------------------------------------------------------
// Auto-sequencing — overwrite `sequence` field with the row index. Returns a
// new record. Used by storage on every persist.
// ---------------------------------------------------------------------------
export function resequenceComponents(rec) {
  if (!rec || !Array.isArray(rec.components)) return rec;
  const comps = rec.components.map((c, i) => ({ ...c, sequence: i }));
  return { ...rec, components: comps };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const _isPosNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
const _isNonNegNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) >= 0;
const _optPosNum = (v) => v === "" || v == null || _isPosNum(v);
const _optNonNegNum = (v) => v === "" || v == null || _isNonNegNum(v);

function _validateComponent(c, idx) {
  const errs = [];
  const tag = `Component #${idx + 1} (${roleLabel(c.role)})`;
  if (!VALID_ROLES.includes(c.role)) {
    errs.push(`${tag}: unknown role "${c.role}".`);
  }
  if (!_optPosNum(c.lengthFt)) {
    errs.push(`${tag}: length must be a positive number if entered.`);
  }
  if (!_optPosNum(c.outerDiameter)) errs.push(`${tag}: OD must be positive if entered.`);
  if (!_optPosNum(c.innerDiameter)) errs.push(`${tag}: ID must be positive if entered.`);
  if (_isPosNum(c.outerDiameter) && _isPosNum(c.innerDiameter) &&
      Number(c.outerDiameter) <= Number(c.innerDiameter))
    errs.push(`${tag}: OD must be greater than ID.`);
  if (c.pieceCount !== "" && c.pieceCount != null) {
    const n = Number(c.pieceCount);
    if (!Number.isInteger(n) || n <= 0)
      errs.push(`${tag}: piece count must be a positive integer.`);
  }
  if (!_optNonNegNum(c.totalWeightLbs))
    errs.push(`${tag}: total weight must be ≥ 0 if entered.`);
  if (!c.componentUid || !String(c.componentUid).trim())
    errs.push(`${tag}: missing permanent componentUid (internal error — re-create the row).`);
  return errs;
}

export function validateBhaRecord(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["BHA record is missing."] };

  const errs = [];

  if (!rec.wellId || !String(rec.wellId).trim())
    errs.push("BHA must be linked to a well.");
  if (!rec.name || !String(rec.name).trim())
    errs.push("BHA name is required.");

  if (!ASSEMBLY_STATUSES.includes(rec.assemblyStatus))
    errs.push(`Assembly status must be one of: ${ASSEMBLY_STATUSES.join(", ")}.`);
  if (!OPERATIONAL_STATUSES.includes(rec.operationalStatus))
    errs.push(`Operational status must be one of: ${OPERATIONAL_STATUSES.join(", ")}.`);

  // Operating window — all optional numerics, with sensible ordering.
  for (const [f, label] of [
    ["depthInFt", "Depth in"], ["depthOutFt", "Depth out"],
    ["rotatingHours", "Rotating hours"], ["totalHours", "Total hours"],
    ["bitSize", "Bit size"],
  ]) {
    if (!_optNonNegNum(rec[f])) errs.push(`${label} must be ≥ 0 if entered.`);
  }
  if (_isNonNegNum(rec.depthInFt) && _isNonNegNum(rec.depthOutFt) &&
      Number(rec.depthInFt) > Number(rec.depthOutFt))
    errs.push("Depth in cannot exceed depth out.");
  if (_isNonNegNum(rec.rotatingHours) && _isNonNegNum(rec.totalHours) &&
      Number(rec.rotatingHours) > Number(rec.totalHours))
    errs.push("Rotating hours cannot exceed total hours.");
  if (rec.startDateTime && rec.endDateTime &&
      String(rec.startDateTime) > String(rec.endDateTime))
    errs.push("Start date must be on or before end date.");

  // Components
  const comps = Array.isArray(rec.components) ? rec.components : [];
  comps.forEach((c, i) => { errs.push(..._validateComponent(c, i)); });

  // componentUid uniqueness within the BHA
  const uids = new Set();
  for (const c of comps) {
    if (c.componentUid && uids.has(c.componentUid))
      errs.push(`Duplicate componentUid "${c.componentUid}" — internal sequencing error.`);
    if (c.componentUid) uids.add(c.componentUid);
  }

  return { ok: errs.length === 0, errors: errs };
}

// ---------------------------------------------------------------------------
// Summary projection — what gets cached in WellProfile.children.bha[].
// Pure function: takes a full BHA, returns a small object.
// ---------------------------------------------------------------------------
export function summariseBha(rec) {
  if (!rec) return null;
  return {
    id: rec.id,
    name: rec.name,
    bhaNumber: rec.bhaNumber,
    revision: rec.revision || "",
    assemblyStatus: rec.assemblyStatus,
    operationalStatus: rec.operationalStatus,
    bitSize: rec.bitSize,
    runNumber: rec.runNumber || "",
    componentCount: Array.isArray(rec.components) ? rec.components.length : 0,
    updatedAt: rec.updatedAt,
  };
}
